import type { SupportedLocale } from '@fleetcore/shared'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import { currentLocale } from '../../i18n'
import { fetchFleetCapabilities, fetchTelemetrySeries, fetchVehicleDtc } from './api'
import type { FleetCapabilityView, TelemetrySeriesView, VehicleDtcView } from './api-types'

/**
 * Diagnostics is not live data and does not poll.
 *
 * A fault code is raised by the decoder when a record carries one; a capability
 * profile moves on the scale of a service visit. Polling either at map cadence would
 * put a query per second on a hypertable to watch a value that changes weekly. A
 * refetch on window focus covers the case that matters — an installer changing a
 * program number and coming back to the tab to check it took.
 */
const DIAGNOSTICS_STALE_MS = 60_000
const CAPABILITY_STALE_MS = 5 * 60_000

/** 7 days of supply rail. Enough mornings to hold several cranks, cheap enough to plot. */
export const VOLTAGE_TREND_DAYS = 7
/** The hard cap the API applies anyway; stated here so the URL says what it wants. */
export const VOLTAGE_TREND_LIMIT = 2_000

export const diagnosticsQueryKeys = {
  vehicleDtc: (vehicleId: string, locale: SupportedLocale) =>
    ['diagnostics', 'dtc', vehicleId, locale] as const,
  fleetCapabilities: (locale: SupportedLocale) => ['diagnostics', 'capabilities', locale] as const,
  series: (vehicleId: string, channel: string, from: string) =>
    ['diagnostics', 'series', vehicleId, channel, from] as const,
}

export function useVehicleDtc(vehicleId: string): UseQueryResult<VehicleDtcView> {
  const locale = currentLocale()
  return useQuery({
    queryKey: diagnosticsQueryKeys.vehicleDtc(vehicleId, locale),
    queryFn: () => fetchVehicleDtc(vehicleId, locale),
    enabled: vehicleId.length > 0,
    staleTime: DIAGNOSTICS_STALE_MS,
    refetchOnWindowFocus: true,
  })
}

export function useFleetCapabilities(): UseQueryResult<FleetCapabilityView> {
  const locale = currentLocale()
  return useQuery({
    queryKey: diagnosticsQueryKeys.fleetCapabilities(locale),
    queryFn: () => fetchFleetCapabilities(locale),
    staleTime: CAPABILITY_STALE_MS,
    refetchOnWindowFocus: true,
  })
}

/**
 * The supply-rail trend behind the battery verdict.
 *
 * `from` is rounded to the hour so that two renders inside the same hour share a cache
 * entry: a key built from `Date.now()` would miss on every render and re-query the
 * hypertable for a chart nobody asked to refresh.
 */
export function voltageTrendWindow(
  nowMs: number,
  days = VOLTAGE_TREND_DAYS,
): {
  from: string
  to: string
} {
  const to = new Date(Math.ceil(nowMs / 3_600_000) * 3_600_000)
  const from = new Date(to.getTime() - days * 86_400_000)
  return { from: from.toISOString(), to: to.toISOString() }
}

export function useVoltageTrend(
  vehicleId: string,
  channel = 'external_voltage',
  nowMs = Date.now(),
): UseQueryResult<TelemetrySeriesView> {
  const locale = currentLocale()
  const window = voltageTrendWindow(nowMs)
  return useQuery({
    queryKey: diagnosticsQueryKeys.series(vehicleId, channel, window.from),
    queryFn: () =>
      fetchTelemetrySeries(
        vehicleId,
        { channel, from: window.from, to: window.to, limit: VOLTAGE_TREND_LIMIT },
        locale,
      ),
    enabled: vehicleId.length > 0,
    staleTime: DIAGNOSTICS_STALE_MS,
  })
}
